// "Ask Winter" tab on the call-detail + demo-detail page (2026-05-08).
//
// Self-contained chat panel that hits POST /api/ask-winter with
// either `callContext: { callId }` or `demoContext: { demoId }` so
// Winter has the recording's transcript + summary as grounding for
// every answer. Conversation history is kept in component state —
// no persistence; closing the tab clears the thread (matches the
// global Ask Winter pill UX).
//
// Both surfaces share the same backend loader (loadCallContextForWinter
// in routes/askWinter.js) since the underlying `demos` table stores
// demo + qualifying_call + meeting recordings. The two prop names
// (callId vs demoId) are kept distinct so callers stay self-documenting.

import { useEffect, useRef, useState } from 'react';
import WinterMarkdown from './WinterMarkdown';
import { streamWinterReply } from '../utils/winterStream';

// Light-theme markdown + activity styles for the call-tab surface
// (the global AskWinter panel carries its own dark-theme block).
const WINTER_TAB_STYLES = `
  @keyframes winterTabToolSpin { to { transform: rotate(360deg); } }
  .winter-tab-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 10px; border-radius: 999px;
    background: #eef2ff; border: 1px solid #c7d2fe;
    color: #4338ca; font-size: 11.5px; font-weight: 600;
  }
  .winter-tab-chip .spin {
    width: 10px; height: 10px; border-radius: 50%; flex: none;
    border: 2px solid #c7d2fe; border-top-color: #4338ca;
    animation: winterTabToolSpin 700ms linear infinite;
  }
  .winter-md-light { font-size: 13px; line-height: 1.55; color: #0f172a; word-break: break-word; }
  .winter-md-light p { margin: 0 0 8px; }
  .winter-md-light p:last-child { margin-bottom: 0; }
  .winter-md-light ul, .winter-md-light ol { margin: 4px 0 8px; padding-left: 20px; }
  .winter-md-light li { margin: 2px 0; }
  .winter-md-light code {
    background: #e2e8f0; border-radius: 4px; padding: 1px 5px; font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }
  .winter-md-light pre {
    background: #0f172a; color: #e2e8f0; border-radius: 8px;
    padding: 10px 12px; overflow-x: auto; margin: 6px 0 8px;
  }
  .winter-md-light pre code { background: none; color: inherit; padding: 0; font-size: 12px; }
  .winter-md-light a { color: #4f46e5; text-decoration: underline; }
  .winter-md-light blockquote {
    margin: 6px 0 8px; padding: 4px 10px;
    border-left: 3px solid #c7d2fe; color: #475569; font-style: italic;
  }
  .winter-md-light hr { border: none; border-top: 1px solid var(--room-border, #e2e8f0); margin: 8px 0; }
  .winter-md-light .wm-h { font-weight: 700; margin: 8px 0 4px; color: #0f172a; }
  .winter-md-light .wm-h1 { font-size: 14.5px; }
  .winter-md-light .wm-h2 { font-size: 14px; }
  .winter-md-light .wm-h3 { font-size: 13.5px; }
  .winter-md-light table {
    border-collapse: collapse; margin: 6px 0 8px; width: 100%;
    font-size: 12.5px; display: block; overflow-x: auto;
  }
  .winter-md-light th, .winter-md-light td {
    border: 1px solid var(--room-border, #e2e8f0); padding: 4px 8px; text-align: left; white-space: nowrap;
  }
  .winter-md-light th { background: #f8fafc; font-weight: 700; }
`;

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const CALL_PROMPTS = [
  'Summarize the key takeaways in 3 bullets',
  'What were the main objections raised?',
  'What action items did we agree to?',
  'How did the prospect react to the pricing discussion?',
  'Draft a 2-paragraph follow-up email based on this call',
];

const DEMO_PROMPTS = [
  'Summarize the demo in 3 bullets',
  'What features got the most engagement?',
  'What objections or questions came up?',
  'Draft a follow-up email I can send to the prospect',
  'What should I emphasize on my next call with this prospect?',
];

// Accepts either `callId` (qualifying-call / meeting detail) or
// `demoId` (demo trailer detail). Sends the right context shape
// to /api/ask-winter so the backend resolves the correct row.
export default function AskWinterCallTab({ callId, demoId, authToken }) {
  const isDemo = !!demoId && !callId;
  const SUGGESTED_PROMPTS = isDemo ? DEMO_PROMPTS : CALL_PROMPTS;
  const headerLabel = isDemo
    ? "Winter's seeded with this demo's transcript + summary. Ask anything about it."
    : "Winter's seeded with this call's transcript + summary. Ask anything about it.";
  const placeholderText = isDemo ? 'Ask Winter about this demo…' : 'Ask Winter about this call…';

  // Per-recording sessionStorage key. Survives tab switches and
  // page refreshes within the browser session, but clears when the
  // tab closes — matches the global Ask Winter pill UX (no
  // long-term persistence). Scoped to the recording id so opening
  // demo A → asking 3 questions → switching to demo B doesn't
  // leak A's thread into B's panel.
  const storageKey = `winter:thread:${isDemo ? 'demo' : 'call'}:${demoId || callId || 'global'}`;
  // Live mirror of storageKey — lets an in-flight send() detect that
  // the panel has been re-pointed at a different recording.
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;
  // Lazy initializer reads from sessionStorage exactly once on
  // mount — cheap, and avoids a flash of empty state for a
  // returning user.
  const [messages, setMessages] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Live streaming state for the in-flight reply.
  const [streamText, setStreamText] = useState('');
  const [streamTools, setStreamTools] = useState([]);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Persist on every messages change. JSON-stringify is cheap at
  // these sizes (Ask Winter threads cap at ~20 turns + ~2k chars
  // per message → maybe 40-80kb worst case, fits well under the
  // ~5mb sessionStorage budget). Catch + ignore quota errors —
  // worst case we lose persistence, the UI keeps working.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (messages.length === 0) {
        window.sessionStorage.removeItem(storageKey);
      } else {
        window.sessionStorage.setItem(storageKey, JSON.stringify(messages));
      }
    } catch { /* quota exceeded / private mode — non-fatal */ }
  }, [messages, storageKey]);

  // Switch threads when the recording changes. The lazy initializer
  // only runs once on mount, so without this effect a stored
  // thread for demo A would leak into demo B if React reused the
  // component instance (which it does when the parent just swaps
  // the demoId prop). Reload from sessionStorage with the new key.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setMessages(Array.isArray(parsed) ? parsed : []);
    } catch { setMessages([]); }
    setDraft('');
    setError(null);
    setLoading(false); // don't carry the old call's "Winter's thinking…" over
    setStreamText('');
    setStreamTools([]);
    // Kill any in-flight request for the recording we just left.
    if (abortRef.current) abortRef.current.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Auto-scroll to the bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text || draft).trim();
    if (!trimmed || loading) return;
    setError(null);
    setLoading(true);
    setStreamText('');
    setStreamTools([]);
    const userMsg = { role: 'user', content: trimmed };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setDraft('');
    // Capture the thread identity at send time. If the user switches
    // recordings while Winter is thinking, the storageKey swaps (and
    // the switch effect aborts the request) — stale updates must not
    // land in the new call's thread.
    const sentKey = storageKey;
    const isStale = () => sentKey !== storageKeyRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    let acc = '';
    try {
      const result = await streamWinterReply({
        apiBase: API_BASE,
        authToken,
        signal: controller.signal,
        body: {
          message: trimmed,
          history: messages, // history BEFORE this turn
          ...(isDemo
            ? { demoContext: { demoId } }
            : { callContext: { callId } }),
        },
        onDelta: (chunk) => {
          if (isStale()) return;
          acc += chunk;
          setStreamText(acc);
          setStreamTools((prev) => (prev.some((t) => !t.done)
            ? prev.map((t) => ({ ...t, done: true }))
            : prev));
        },
        onTool: (tool) => {
          if (isStale()) return;
          setStreamTools((prev) => [
            ...prev.map((t) => ({ ...t, done: true })),
            { ...tool, done: false },
          ]);
        },
      });
      if (isStale()) return; // reply belongs to a recording we've left
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply || '' }]);
    } catch (err) {
      if (err && err.name === 'AbortError') {
        // Thread switch killed it — the switch effect already reset state.
        return;
      }
      if (!isStale()) setError(err.message);
      // Keep the user message in the thread so they can see what
      // they asked + the error context.
    } finally {
      if (!isStale()) {
        setLoading(false);
        setStreamText('');
        setStreamTools([]);
      }
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const onSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    send();
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl + Enter to send (avoids accidental sends on a stray
    // newline).
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%', minHeight: 380 }}>
      <style>{WINTER_TAB_STYLES}</style>
      {/* Header — small intro line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '4px 0',
        fontSize: 12, color: 'var(--room-text-muted)',
      }}>
        <span>{headerLabel}</span>
      </div>

      {/* Thread */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 8,
          padding: '4px 2px',
        }}
      >
        {messages.length === 0 && !loading && (
          <div style={{
            padding: '20px 12px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--room-text-subtle)',
            }}>
              Try asking
            </div>
            {SUGGESTED_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => send(p)}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--room-border, #e2e8f0)',
                  background: 'var(--room-card, #ffffff)',
                  color: 'var(--room-text-strong, #0f172a)',
                  fontSize: 13, fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'background 120ms ease, border-color 120ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--room-surface, #f8fafc)';
                  e.currentTarget.style.borderColor = 'var(--room-border, #cbd5e1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--room-card, #ffffff)';
                  e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)';
                }}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const bubbleStyle = {
            alignSelf: isUser ? 'flex-end' : 'flex-start',
            maxWidth: '90%',
            padding: '8px 12px',
            borderRadius: 12,
            fontSize: 13, lineHeight: 1.5,
            background: isUser ? '#6366f1' : 'var(--room-border-soft, #f1f5f9)',
            color: isUser ? 'white' : 'var(--room-text-strong, #0f172a)',
          };
          // User text is plain (they typed it); assistant text gets the
          // full markdown treatment (lists, tables, quotes, code) —
          // Winter's data answers regularly use all of them.
          if (isUser) {
            return (
              <div
                key={`${m.role}-${i}`}
                style={{ ...bubbleStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {m.content}
              </div>
            );
          }
          return (
            <WinterMarkdown
              key={`${m.role}-${i}`}
              className="winter-md-light"
              text={m.content}
              style={bubbleStyle}
            />
          );
        })}

        {/* In-flight reply: tool-activity chips + live streaming text */}
        {loading && (
          <div style={{
            alignSelf: 'flex-start', maxWidth: '90%',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            {streamTools.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {streamTools.map((t, i) => (
                  <span key={`${t.name}-${i}`} className="winter-tab-chip">
                    {t.done ? <span aria-hidden="true">✓</span> : <span className="spin" aria-hidden="true" />}
                    {t.label}
                  </span>
                ))}
              </div>
            )}
            <div style={{
              padding: '8px 12px',
              borderRadius: 12,
              background: 'var(--room-border-soft, #f1f5f9)',
              color: 'var(--room-text-strong, #0f172a)',
              fontSize: 13,
            }}>
              {streamText ? (
                <WinterMarkdown className="winter-md-light" text={streamText} />
              ) : (
                <span style={{ color: 'var(--room-text-muted, #64748b)' }}>
                  {streamTools.some((t) => !t.done) ? 'Working on it…' : "Winter's thinking…"}
                </span>
              )}
            </div>
          </div>
        )}

        {error && (
          <div style={{
            alignSelf: 'stretch',
            padding: '8px 12px',
            borderRadius: 8,
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#dc2626',
            fontSize: 12,
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholderText}
          disabled={loading}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 8,
            fontSize: 13, fontFamily: 'inherit',
          }}
        />
        <button
          type="submit"
          disabled={!draft.trim() || loading}
          style={{
            padding: '10px 16px',
            border: 'none', borderRadius: 8,
            background: !draft.trim() || loading ? 'rgba(99, 102, 241, 0.4)' : '#6366f1',
            color: 'white', fontSize: 13, fontWeight: 600,
            fontFamily: 'inherit',
            cursor: !draft.trim() || loading ? 'not-allowed' : 'pointer',
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
}
